import { useState } from 'react';
import type { MouseEvent } from 'react';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from '~/components/ui/dialog';

const LINKEDIN = 'https://www.linkedin.com/in/fareal';
const GITHUB = 'https://github.com/farealahmed';

export default function Footer() {
  const [privacyOpen, setPrivacyOpen] = useState(false);
  const [termsOpen, setTermsOpen] = useState(false);

  const scrollToCurriculum = (e: MouseEvent) => {
    e.preventDefault();
    const el = document.getElementById('curriculum');
    if (el) {
      el.scrollIntoView({ behavior: 'smooth' });
    }
  };

  return (
    <footer className="bg-bg-elevated border-t border-border-subtle">
      <div className="max-w-content mx-auto px-6 lg:px-10 py-10">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-10">
          {/* Left: Platform name + description */}
          <div>
            <div className="flex items-center gap-2 mb-3">
              <span className="font-heading text-lg font-medium text-text-primary">
                LearnEasy
              </span>
              <span className="font-heading text-xs text-text-secondary">
{/*                 Agentic Software Engineering */}
              </span>
            </div>
            <p className="font-body text-sm text-text-secondary max-w-sm">
              The complete course for software engineers.
            </p>
          </div>

          {/* Right: Link columns */}
          <div className="grid grid-cols-3 gap-6">
            {/* Course */}
            <div>
              <h4 className="font-heading text-xs font-medium text-text-primary uppercase tracking-wider mb-3">
                Course
              </h4>
              <ul className="space-y-2">
                <li>
                  <a
                    href="/#curriculum"
                    onClick={scrollToCurriculum}
                    className="font-body text-sm text-text-secondary hover:text-text-primary transition-colors duration-200 no-underline"
                  >
                    Curriculum
                  </a>
                </li>
                <li>
                  <a
                    href="/#pricing"
                    className="font-body text-sm text-text-secondary hover:text-text-primary transition-colors duration-200 no-underline"
                  >
                    Pricing
                  </a>
                </li>
              </ul>
            </div>

            {/* Legal */}
            <div>
              <h4 className="font-heading text-xs font-medium text-text-primary uppercase tracking-wider mb-3">
                Legal
              </h4>
              <ul className="space-y-2">
                <li>
                  <button
                    onClick={() => setPrivacyOpen(true)}
                    className="font-body text-sm text-text-secondary hover:text-text-primary transition-colors duration-200 bg-transparent border-none p-0 cursor-pointer"
                  >
                    Privacy
                  </button>
                </li>
                <li>
                  <button
                    onClick={() => setTermsOpen(true)}
                    className="font-body text-sm text-text-secondary hover:text-text-primary transition-colors duration-200 bg-transparent border-none p-0 cursor-pointer"
                  >
                    Terms
                  </button>
                </li>
              </ul>
            </div>

            {/* Connect */}
            <div>
              <h4 className="font-heading text-xs font-medium text-text-primary uppercase tracking-wider mb-3">
                Connect
              </h4>
              <ul className="space-y-2">
                <li>
                  <a
                    href={LINKEDIN}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="font-body text-sm text-text-secondary hover:text-text-primary transition-colors duration-200 no-underline"
                  >
                    LinkedIn
                  </a>
                </li>
                <li>
                  <a
                    href={GITHUB}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="font-body text-sm text-text-secondary hover:text-text-primary transition-colors duration-200 no-underline"
                  >
                    GitHub
                  </a>
                </li>
              </ul>
            </div>
          </div>
        </div>

        {/* Bottom */}
        <div className="mt-10 pt-6 border-t border-border-subtle">
          <p className="font-body text-xs text-text-muted text-center">
            &copy; 2026 Fareal Ahmed. All rights reserved.
          </p>
        </div>
      </div>

      {/* Privacy Modal */}
      <Dialog open={privacyOpen} onOpenChange={setPrivacyOpen}>
        <DialogContent className="bg-bg-elevated border-border-subtle text-text-primary max-w-lg max-h-[80vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="font-heading text-h4 text-text-primary">
              Privacy Policy
            </DialogTitle>
            <DialogDescription className="font-body text-sm text-text-secondary">
              Last updated: May 2026
            </DialogDescription>
          </DialogHeader>
          <div className="font-body text-sm text-text-secondary space-y-4 mt-2">
            <p>
              Your privacy is important. This policy explains what information is collected and how it is used.
            </p>
            <h3 className="font-heading text-sm font-medium text-text-primary">Information We Collect</h3>
            <p>
              When you create an account, we collect your email address. This is used solely to authenticate you, grant access to course content, and send important updates about the course.
            </p>
            <h3 className="font-heading text-sm font-medium text-text-primary">Email Privacy</h3>
            <p>
              <strong className="text-text-primary">Your email address will never be sold, rented, or shared with any third party.</strong> We do not use your email for marketing purposes beyond course-related communications. You will not receive spam or unsolicited offers from us or anyone else through this platform.
            </p>
            {/* Stripe hidden — uncomment Payment Information section to restore:
            <h3 className="font-heading text-sm font-medium text-text-primary">Payment Information</h3>
            <p>
              All payments are processed securely through Stripe. We do not store your credit card details on our servers.
            </p>
            */}
            <h3 className="font-heading text-sm font-medium text-text-primary">Contact</h3>
            <p>
              If you have any questions about this privacy policy, reach out via{' '}
              <a href={LINKEDIN} target="_blank" rel="noopener noreferrer" className="text-accent-cyan hover:underline">LinkedIn</a>.
            </p>
          </div>
        </DialogContent>
      </Dialog>

      {/* Terms Modal */}
      <Dialog open={termsOpen} onOpenChange={setTermsOpen}>
        <DialogContent className="bg-bg-elevated border-border-subtle text-text-primary max-w-lg max-h-[80vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="font-heading text-h4 text-text-primary">
              Terms of Service
            </DialogTitle>
            <DialogDescription className="font-body text-sm text-text-secondary">
              Last updated: May 2026
            </DialogDescription>
          </DialogHeader>
          <div className="font-body text-sm text-text-secondary space-y-4 mt-2">
            <p>
              By purchasing and accessing this course, you agree to the following terms.
            </p>
            <h3 className="font-heading text-sm font-medium text-text-primary">Non-Refundable</h3>
            <p>
              <strong className="text-text-primary">All purchases are final. Payments are non-refundable.</strong> Please review the course description, curriculum, and preview content carefully before purchasing.
            </p>
            <h3 className="font-heading text-sm font-medium text-text-primary">Single-User License</h3>
            <p>
              Your purchase grants you a personal, non-transferable license to access and download the course content. The course is intended for use by a single individual on their personal machine(s).
            </p>
            <h3 className="font-heading text-sm font-medium text-text-primary">No Sharing or Redistribution</h3>
            <p>
              You may not share your account credentials, download links, or course materials with others. You may not resell, redistribute, or publicly distribute any part of the course content.
            </p>
            <h3 className="font-heading text-sm font-medium text-text-primary">Intellectual Property</h3>
            <p>
              All course content — including videos, slides, code, and written materials — is the intellectual property of Fareal Ahmed and is protected by copyright law.
            </p>
            <h3 className="font-heading text-sm font-medium text-text-primary">Contact</h3>
            <p>
              For questions about these terms, reach out via{' '}
              <a href={LINKEDIN} target="_blank" rel="noopener noreferrer" className="text-accent-cyan hover:underline">LinkedIn</a>.
            </p>
          </div>
        </DialogContent>
      </Dialog>
    </footer>
  );
}
